import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const weightMngFollowupQuestions = [
  {
    label: 'What was your sex assigned at birth?',
    field: 'weightMngFollowup_sex',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'male', value: 'Male' },
        { key: 'female', value: 'Female' }
      ]
    },
    extras: { sendToBeluga: false }
  },
  {
    label: 'What is your:',
    field: 'weightMngFollowup_weightHeightBMI',
    type: 'info',
    required: false,
    answer: {
      type: 'bmi',
      showLabel: true,
      bmiLabel: 'Your BMI is',
      heightLabel: 'What is your height in feet and inches?',
      weightLabel: 'What is your weight in pounds?'
    }
  },
  {
    field: 'weightMngFollowup_healthChanges',
    label:
      'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'weightMngFollowup_healthChangesDetails',
    label: 'Please tell us more about the changes in your health',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightMngFollowup_healthChangesDetails'
    }
  },
  {
    field: 'weightMngFollowup_medicationResponse',
    label: 'How are you doing on the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Well', key: 'well' },
        { value: 'Not well', key: 'not_well' }
      ]
    }
  },
  {
    field: 'weightMngFollowup_medicationResponseDetails',
    label: 'Please tell us more about what you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightMngFollowup_medicationResponseDetails'
    }
  },
  {
    field: 'weightMngFollowup_sideEffects',
    label: 'Have experienced any side effects from the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'weightMngFollowup_sideEffectsDetails',
    label: 'Please tell us more about the side effects you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightMngFollowup_sideEffectsDetails'
    }
  },
  {
    field: 'weightMngFollowup_treatmentPreference',
    label:
      'Would you like to continue your current treatment, change the dose, or change the medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            "I'm doing well and I would like to continue what I'm on",
          key: 'continue'
        },
        {
          value: 'I like the medication but I would like to increase the dose',
          key: 'increase_dose'
        },
        {
          value: 'I like the medication but I would like to decrease the dose',
          key: 'decrease_dose'
        },
        {
          value: 'I would like to change the medication',
          key: 'change_medication'
        },
        {
          value:
            "I'm not sure, have the provider reach out to discuss further",
          key: 'provider_discussion'
        }
      ]
    }
  },
  {
    field: 'weightMngFollowup_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightMngFollowup_additionalQuestions'
    }
  },
  {
    field: 'weightMngFollowup_truthfulnessConsent',
    label: 'Consent (Truthfulness):',
    type: 'select',
    educationalSlide:
      'Please attest to the following confirming that all information you have provided to us is true and complete.\n\nI verify that I am the patient and that I have answered the questions asked in this intake form.  I confirm that I have reviewed and understood all the questions asked of me.  I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor.  I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'weightMngFollowup_treatmentConsent',
    label: 'Consent (Treatment with Weight Management Pharmacotherapy):',
    type: 'select',
    educationalSlide:
      "Please read the following information to understand the potential benefits and risks. These medications are designed to support the healthy lifestyle changes you are making. Aim to limit processed foods, particularly those high in added sugars, and try to avoid consuming calories through beverages such as soda, juice, or alcohol unless you're having nutrient-rich smoothies. Eating smaller, well-portioned meals is recommended. Keep in mind that even though artificial sweeteners are marketed as \"zero calories,\" they have been linked to an increase in waist size. It's important to engage in exercise on most days, accumulating at least 150 minutes of activity per week, spread out over those days. Since weight loss can result in the loss of not just fat, but also muscle and bone, incorporate both cardiovascular and resistance training into your routine to help build and maintain muscle and bone strength. Additionally, stay well hydrated, as dehydration is a potential side effect of weight loss medications, and ensure you get adequate sleep to support both your physical and mental well-being.\n\nIndication for Use:\nYou are considering treatment with one or more weight management medications, which may include metformin, bupropion, naltrexone, acarbose, SGLT2 inhibitors, and/or orlistat, as part of your personalized plan for managing overweight or obesity. These medications work through different mechanisms—improving insulin sensitivity, modulating appetite and mood, increasing glucose excretion through urine, reducing carbohydrate absorption, or inhibiting fat absorption—to help achieve weight loss and improve metabolic health.\n\nPotential Benefits:\n- Achievement of weight loss or more effective weight management\n- Improved blood glucose control and insulin sensitivity\n- Enhanced overall metabolic health\n- Potential reduction in cardiovascular risk factors\n\nPotential Side Effects:\nMetformin\n Common: Gastrointestinal upset (nausea, vomiting, diarrhea), decreased appetite\n Serious (rare): Lactic acidosis, vitamin B12 deficiency over long-term use\n\nBupropion\n Common: Dry mouth, headache, insomnia, nausea, anxiety\n Serious: Increased risk of seizures (especially in individuals with predisposing factors), elevated blood pressure\n\nNaltrexone\n Common: Nausea, headache, dizziness, fatigue\n Serious: Potential liver enzyme elevations or liver injury at high doses\n\nAcarbose\n Common: Gastrointestinal discomfort including flatulence, diarrhea, bloating, and abdominal pain\n Serious: Rare severe gastrointestinal events\n\nSGLT2 Inhibitors (e.g., canagliflozin, dapagliflozin, empagliflozin)\n Common: Increased urination, genital yeast infections, urinary tract infections, mild dehydration\n Serious: Risk of diabetic ketoacidosis (rare, even in non-diabetics), Fournier's gangrene (a rare serious genital infection), volume depletion, increased risk of lower limb amputation (mainly with canagliflozin in some studies)\n\nOrlistat\n Common: Oily spotting, increased frequency of bowel movements, flatulence with discharge, possible reduction in absorption of fat-soluble vitamins\n Serious: Rare reports of liver injury\n\nRisks and Considerations:\n Certain conditions or medical histories may make one or more of these medications unsuitable.\n- Metformin is contraindicated in severe kidney impairment or a history of metabolic acidosis.\n- Bupropion should be avoided in those with seizure disorders or a history of eating disorders\n- Naltrexone is not recommended for patients using opioids or with opioid dependence. Please note, this is being prescribed specifically for weight management and not for any addiction-related diagnosis. Some life or disability insurance providers may see naltrexone use as a flag for prior substance use. If you are applying for insurance soon, consider completing that process before starting this medication\n- Acarbose should not be used in individuals with inflammatory bowel disease, colonic ulceration, or a history of intestinal obstruction.\nSGLT2 inhibitors are not recommended in individuals with a history of frequent urinary tract infections, recurrent yeast infections, or those prone to dehydration. They should be used cautiously in individuals with kidney disease or low blood pressure.\n- Orlistat is contraindicated in those with chronic malabsorption syndromes or cholestasis.\n\nMonitoring and Follow-Up:\nOngoing monitoring is essential; follow-up visits will be scheduled to assess treatment efficacy and review any side effects. In some cases, additional assessments (such as digital photographs to verify weight changes) may be requested to ensure consistency in reporting.\n\nBy selecting one of the options below, you acknowledge that you have read and understand the potential benefits, side effects, risks, and monitoring requirements associated with weight management pharmacotherapy and that you consent to proceed with the treatment plan as discussed.",
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read and understand the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information and I wish to continue',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'weightMngFollowup_complete',
    isLast: true,
    label: `Thank you for completing this questionnaire, please select "Finish" to submit`,
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'weightMngFollowup_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    answer: {},
    blockQuestionFlow: true,
    type: 'info',
    isLast: true
  }
] as const;

export type WeightManagementFollowupFieldAnswers = Partial<
  QuestionAnswerMap<typeof weightMngFollowupQuestions>
>;
export default weightMngFollowupQuestions
